import React, { useState, useEffect, useCallback } from 'react'
import { proxyAPI, platformAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import {
  Globe,
  RefreshCw,
  ShieldCheck,
  Link2,
  Unlink,
  CheckCircle2,
  XCircle,
  Loader2,
  Server,
  AlertTriangle,
} from 'lucide-react'

export const SystemProxiesPage = () => {
  const { isOwner } = useAuth()
  const [proxies, setProxies] = useState([])
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(null)
  const [actionMessage, setActionMessage] = useState(null)

  // For the assign modal
  const [assignModal, setAssignModal] = useState(null)
  const [tenants, setTenants] = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [tenantSearch, setTenantSearch] = useState('')

  const fetchProxies = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await proxyAPI.getAll()
      setProxies(response.data.data || [])
      setSummary(response.data.summary || null)
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Falha ao carregar os proxies')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchTenants = useCallback(async () => {
    try {
      const response = await platformAPI.getTenants({ per_page: 100, status: 'active' })
      setTenants(response.data.data || [])
    } catch {
      // non-critical
    }
  }, [])

  useEffect(() => {
    fetchProxies()
    fetchTenants()
  }, [fetchProxies, fetchTenants])

  const showMessage = (msg) => {
    setActionMessage(msg)
    setTimeout(() => setActionMessage(null), 4000)
  }

  const handleSync = async () => {
    setActionLoading('sync')
    try {
      const res = await proxyAPI.sync()
      showMessage(`Sincronizado: ${res.data.data.created} criados, ${res.data.data.updated} atualizados`)
      fetchProxies()
    } catch (err) {
      showMessage(err.response?.data?.error?.message || 'Falha na sincronização')
    } finally {
      setActionLoading(null)
    }
  }

  const handleValidate = async () => {
    setActionLoading('validate')
    try {
      const res = await proxyAPI.validate()
      const d = res.data.data
      showMessage(`Validado: ${d.now_valid} recuperados, ${d.now_invalid} caíram, ${d.removed_from_remote} removidos`)
      fetchProxies()
    } catch (err) {
      showMessage(err.response?.data?.error?.message || 'Falha na validação')
    } finally {
      setActionLoading(null)
    }
  }

  const handleUnassign = async (proxyId) => {
    setActionLoading(`unassign-${proxyId}`)
    try {
      await proxyAPI.unassign(proxyId)
      showMessage('Proxy desvinculado')
      fetchProxies()
    } catch (err) {
      showMessage(err.response?.data?.error?.message || 'Falha ao desvincular')
    } finally {
      setActionLoading(null)
    }
  }

  const openAssignModal = (proxy) => {
    setAssignModal(proxy)
    setSelectedTenant('')
    setTenantSearch('')
  }

  const handleAssign = async () => {
    if (!selectedTenant || !assignModal) return
    setActionLoading(`assign-${assignModal.id}`)
    try {
      await proxyAPI.assign(assignModal.id, selectedTenant)
      showMessage('Proxy atribuído')
      setAssignModal(null)
      fetchProxies()
    } catch (err) {
      showMessage(err.response?.data?.error?.message || 'Falha ao atribuir')
    } finally {
      setActionLoading(null)
    }
  }

  const filteredTenants = tenants.filter(t =>
    !tenantSearch ||
    t.name?.toLowerCase().includes(tenantSearch.toLowerCase()) ||
    t.slug?.toLowerCase().includes(tenantSearch.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-admin-900">Gerenciamento de proxies</h1>
          <p className="text-sm text-admin-600 mt-1">Pool de proxies da Webshare para as sessões de WhatsApp</p>
        </div>
        <div className="flex gap-2">
          {isOwner && (
          <button
            onClick={handleSync}
            disabled={actionLoading === 'sync'}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-500 disabled:opacity-50"
          >
            {actionLoading === 'sync' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sincronizar com a Webshare
          </button>
          )}
          {isOwner && (
          <button
            onClick={handleValidate}
            disabled={actionLoading === 'validate'}
            className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-admin-900 ring-1 ring-inset ring-admin-300 hover:bg-admin-50 disabled:opacity-50"
          >
            {actionLoading === 'validate' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Validar status
          </button>
          )}
        </div>
      </div>

      {/* Action message */}
      {actionMessage && (
        <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
          {actionMessage}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-md bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="card p-4">
            <p className="text-xs font-medium text-admin-500 uppercase">Total</p>
            <p className="text-2xl font-bold text-admin-900 mt-1">{summary.total}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs font-medium text-green-600 uppercase">Disponíveis</p>
            <p className="text-2xl font-bold text-green-700 mt-1">{summary.available}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs font-medium text-blue-600 uppercase">Atribuídos</p>
            <p className="text-2xl font-bold text-blue-700 mt-1">{summary.assigned}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs font-medium text-red-600 uppercase">Inválidos</p>
            <p className="text-2xl font-bold text-red-700 mt-1">{summary.invalid}</p>
          </div>
        </div>
      )}

      {/* Proxy table */}
      <div className="card">
        <div className="overflow-x-auto">
          {proxies.length === 0 ? (
            <div className="text-center py-12">
              <Server className="h-12 w-12 text-admin-400 mx-auto mb-4" />
              <h3 className="text-sm font-semibold text-admin-900">Nenhum proxy</h3>
              <p className="text-sm text-admin-600 mt-1">
                Clique em "Sincronizar com a Webshare" para buscar sua lista de proxies.
              </p>
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-admin-50">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-admin-700 text-sm">IP</th>
                  <th className="text-left py-3 px-4 font-semibold text-admin-700 text-sm">Porta</th>
                  <th className="text-left py-3 px-4 font-semibold text-admin-700 text-sm">Localização</th>
                  <th className="text-left py-3 px-4 font-semibold text-admin-700 text-sm">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-admin-700 text-sm">Atribuído a</th>
                  <th className="text-right py-3 px-4 font-semibold text-admin-700 text-sm">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-admin-200">
                {proxies.map((proxy) => (
                  <tr key={proxy.id} className="hover:bg-admin-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-mono text-admin-900">
                      {proxy.proxy_address}
                    </td>
                    <td className="py-3 px-4 text-sm text-admin-600">{proxy.port}</td>
                    <td className="py-3 px-4 text-sm text-admin-600">
                      {proxy.city_name ? `${proxy.city_name}, ` : ''}
                      {proxy.country_code || '-'}
                    </td>
                    <td className="py-3 px-4">
                      {proxy.valid ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                          <CheckCircle2 className="h-3 w-3" /> Válido
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                          <XCircle className="h-3 w-3" /> Inválido
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {proxy.tenant_id ? (
                        <a
                          href={`/tenants/${proxy.tenant_id}`}
                          className="text-primary-600 hover:underline font-medium"
                        >
                          {proxy.tenant_name || proxy.tenant_slug || proxy.tenant_id}
                        </a>
                      ) : (
                        <span className="text-admin-400">Não atribuído</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {!isOwner ? null : proxy.tenant_id ? (
                        <button
                          onClick={() => handleUnassign(proxy.id)}
                          disabled={actionLoading === `unassign-${proxy.id}`}
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                        >
                          {actionLoading === `unassign-${proxy.id}` ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Unlink className="h-3 w-3" />
                          )}
                          Desvincular
                        </button>
                      ) : (
                        <button
                          onClick={() => openAssignModal(proxy)}
                          disabled={!proxy.valid}
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                        >
                          <Link2 className="h-3 w-3" /> Atribuir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Assign Modal */}
      {assignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-admin-900 mb-1">Atribuir proxy</h3>
            <p className="text-sm text-admin-600 mb-4">
              {assignModal.proxy_address}:{assignModal.port}
            </p>

            <input
              type="text"
              placeholder="Buscar estúdios..."
              value={tenantSearch}
              onChange={(e) => setTenantSearch(e.target.value)}
              className="w-full rounded-md border border-admin-300 px-3 py-2 text-sm mb-2 focus:ring-primary-500 focus:border-primary-500"
            />

            <div className="max-h-48 overflow-y-auto border rounded-md divide-y divide-admin-100">
              {filteredTenants.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSelectedTenant(t.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-admin-50 ${
                    selectedTenant === t.id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-admin-700'
                  }`}
                >
                  {t.name} <span className="text-admin-400">({t.slug})</span>
                </button>
              ))}
              {filteredTenants.length === 0 && (
                <p className="px-3 py-4 text-sm text-admin-400 text-center">Nenhum estúdio encontrado</p>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setAssignModal(null)}
                className="rounded-md px-4 py-2 text-sm font-medium text-admin-700 ring-1 ring-inset ring-admin-300 hover:bg-admin-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedTenant || actionLoading?.startsWith('assign')}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-500 disabled:opacity-50"
              >
                {actionLoading?.startsWith('assign') ? 'Atribuindo...' : 'Atribuir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
